// src/hooks/useWebChat.ts
import { NovuError } from "@novu/js";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useDataRef } from "./internal/useDataRef.js";
import { useNovu } from "./NovuProvider.js";
var EMPTY_SERVER_SNAPSHOT = {
  key: "ssr",
  status: "loading",
  run: { isRunning: false },
  conversationStatus: "active",
  pagination: { hasMore: false, status: "idle" },
  messages: [],
  pendingActions: [],
  isRecovering: false
};
function handlePublicationCallbacks(args) {
  const {
    snapshot,
    meta,
    conversationIdProp,
    propsRef,
    loadNotifiedRef,
    notifiedCatchUpErrorRef,
    lastReportedErrorKeyRef
  } = args;
  if ((meta?.historyLoaded || meta?.change?.kind === "history") && !loadNotifiedRef.current && conversationIdProp) {
    if (snapshot.conversationId) {
      loadNotifiedRef.current = true;
      propsRef.current.onSuccess?.({
        conversationId: snapshot.conversationId,
        messages: [...snapshot.messages],
        hasMore: snapshot.pagination.hasMore
      });
    }
  }
  if (snapshot.catchUpError && snapshot.catchUpError !== notifiedCatchUpErrorRef.current) {
    notifiedCatchUpErrorRef.current = snapshot.catchUpError;
    propsRef.current.onError?.(snapshot.catchUpError);
  } else if (!snapshot.catchUpError) {
    notifiedCatchUpErrorRef.current = void 0;
  }
  if (snapshot.error) {
    const originalMessage = "originalError" in snapshot.error ? snapshot.error.originalError?.message ?? "" : "";
    const errorKey = `${snapshot.error.message}:${originalMessage}`;
    if (lastReportedErrorKeyRef.current !== errorKey) {
      lastReportedErrorKeyRef.current = errorKey;
      propsRef.current.onError?.(snapshot.error);
    }
  } else {
    lastReportedErrorKeyRef.current = void 0;
  }
  const change = meta?.change;
  if (change?.kind === "live") {
    propsRef.current.onEvent?.(change.envelope);
  }
  if (change && change.kind !== "history") {
    for (const message of change.addedMessages) {
      propsRef.current.onMessage?.(message);
    }
  }
  if (change) {
    for (const action of change.newActions) {
      propsRef.current.onActionRequested?.(action);
    }
  }
}
function getCreateFlowKey(agentId, agentHash) {
  return `${agentId}\0${agentHash ?? ""}`;
}
function getManagedRuntimeKey(agentId, agentHash, conversationIdProp, createEpoch) {
  if (conversationIdProp) {
    return `resume:${agentId}\0${agentHash ?? ""}\0${conversationIdProp}`;
  }
  return `${getCreateFlowKey(agentId, agentHash)}\0${createEpoch}`;
}
function toNovuError(error) {
  if (error instanceof NovuError) {
    return error;
  }
  if (error instanceof Error) {
    return new NovuError("Failed to load Web Chat", error);
  }
  return new NovuError("Failed to load Web Chat", new Error(String(error)));
}
var useWebChat = (props) => {
  const novu = useNovu();
  const propsRef = useDataRef(props);
  const [loadState, setLoadState] = useState(() => ({
    novu,
    status: "loading"
  }));
  useEffect(() => {
    let cancelled = false;
    if (!novu.isWebChatLoaded) {
      setLoadState({ novu, status: "loading" });
    }
    void novu.loadWebChat().then(() => {
      if (!cancelled) {
        setLoadState({ novu, status: "ready" });
      }
    }).catch((error) => {
      if (cancelled) {
        return;
      }
      const novuError = toNovuError(error);
      propsRef.current.onError?.(novuError);
      setLoadState({ novu, status: "error", error: novuError });
    });
    return () => {
      cancelled = true;
    };
  }, [novu, propsRef]);
  const webChatReady = loadState.novu === novu && loadState.status === "ready";
  const webChatLoadError = loadState.novu === novu && loadState.status === "error" ? loadState.error : void 0;
  const isWebChatLoading = loadState.novu !== novu || loadState.status === "loading";
  const sharedRuntime = "conversation" in props ? props.conversation : void 0;
  const agentId = sharedRuntime?.agentId ?? props.agentId;
  const conversationIdProp = sharedRuntime ? void 0 : props.conversationId;
  const agentHash = sharedRuntime ? void 0 : props.agentHash;
  const ownedRuntimeRef = useRef(null);
  const [managedRuntime, setManagedRuntime] = useState(null);
  const [createEpoch, setCreateEpoch] = useState(0);
  const managedRuntimeKey = sharedRuntime ? null : getManagedRuntimeKey(agentId, agentHash, conversationIdProp, createEpoch);
  useEffect(() => {
    if (sharedRuntime) {
      ownedRuntimeRef.current?.runtime.dispose();
      ownedRuntimeRef.current = null;
      setManagedRuntime(null);
      return;
    }
    if (!webChatReady || !managedRuntimeKey) {
      setManagedRuntime(null);
      return;
    }
    const key = managedRuntimeKey;
    const current = ownedRuntimeRef.current;
    let runtime2;
    if (current?.novu === novu && current.key === key) {
      runtime2 = current.runtime;
    } else {
      current?.runtime.dispose();
      runtime2 = conversationIdProp ? novu.webChat.conversation({ agentId, conversationId: conversationIdProp, agentHash }) : novu.webChat.conversation({ agentId, agentHash });
      ownedRuntimeRef.current = { key, novu, runtime: runtime2 };
    }
    setManagedRuntime({ key, runtime: runtime2 });
    return () => {
      ownedRuntimeRef.current?.runtime.dispose();
      ownedRuntimeRef.current = null;
      setManagedRuntime(null);
    };
  }, [webChatReady, sharedRuntime, novu, agentId, conversationIdProp, agentHash, createEpoch, managedRuntimeKey]);
  const runtime = sharedRuntime ?? (managedRuntime?.key === managedRuntimeKey ? managedRuntime.runtime : null);
  const loadNotifiedRef = useRef(false);
  const replayedActionsRef = useRef(false);
  const notifiedCatchUpErrorRef = useRef();
  const lastReportedErrorKeyRef = useRef();
  useEffect(() => {
    loadNotifiedRef.current = false;
    replayedActionsRef.current = false;
    notifiedCatchUpErrorRef.current = void 0;
    lastReportedErrorKeyRef.current = void 0;
  }, [runtime]);
  const subscribe = useCallback(
    (onStoreChange) => {
      if (!runtime) {
        return () => {
        };
      }
      if (!replayedActionsRef.current) {
        replayedActionsRef.current = true;
        for (const action of runtime.getSnapshot().pendingActions) {
          propsRef.current.onActionRequested?.(action);
        }
      }
      return runtime.subscribe((snapshot2, meta) => {
        onStoreChange();
        handlePublicationCallbacks({
          snapshot: snapshot2,
          meta,
          conversationIdProp,
          propsRef,
          loadNotifiedRef,
          notifiedCatchUpErrorRef,
          lastReportedErrorKeyRef
        });
      });
    },
    [runtime, conversationIdProp, propsRef]
  );
  const getSnapshot = useCallback(() => {
    return runtime?.getSnapshot() ?? EMPTY_SERVER_SNAPSHOT;
  }, [runtime]);
  const getServerSnapshot = useCallback(() => {
    return runtime?.getServerSnapshot() ?? EMPTY_SERVER_SNAPSHOT;
  }, [runtime]);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const callRuntime = useCallback(
    async (action) => {
      if (!runtime) {
        const error = webChatLoadError ?? new NovuError(
          isWebChatLoading ? "Web Chat is still loading" : "Web Chat runtime is unavailable",
          new Error("Web Chat runtime is not ready")
        );
        propsRef.current.onError?.(error);
        return { error };
      }
      const response = await action(runtime);
      if (response.error) {
        propsRef.current.onError?.(response.error);
      }
      return response;
    },
    [runtime, webChatLoadError, isWebChatLoading, propsRef]
  );
  const refetch = useCallback(async () => {
    if (!runtime) {
      return;
    }
    const response = await runtime.load();
    if (response.data) {
      propsRef.current.onSuccess?.({
        conversationId: response.data.conversationId,
        messages: [...response.data.messages],
        hasMore: response.data.hasMore
      });
    }
  }, [runtime, propsRef]);
  const fetchMore = useCallback(async () => {
    const response = await callRuntime((target) => target.fetchMore());
    return {
      ...response,
      error: response.error,
      data: response.data ? {
        messages: [...response.data.messages],
        hasMore: response.data.hasMore
      } : void 0
    };
  }, [callRuntime]);
  const paginationWithFetch = useMemo(
    () => ({
      status: snapshot.pagination.status,
      hasMore: snapshot.pagination.hasMore,
      fetchMore
    }),
    [snapshot.pagination.status, snapshot.pagination.hasMore, fetchMore]
  );
  const sendMessage = useCallback(
    (input) => callRuntime((target) => target.sendMessage(input)),
    [callRuntime]
  );
  const respondToAction = useCallback(
    (args) => callRuntime((target) => target.respondToAction(args)),
    [callRuntime]
  );
  const sendAction = useCallback(
    (args) => callRuntime((target) => target.sendAction(args)),
    [callRuntime]
  );
  const retryMessage = useCallback(
    (messageId) => callRuntime((target) => target.retryMessage(messageId)),
    [callRuntime]
  );
  const startNewConversation = useCallback(() => {
    if (sharedRuntime || conversationIdProp) {
      return;
    }
    setCreateEpoch((epoch) => epoch + 1);
  }, [sharedRuntime, conversationIdProp]);
  return {
    messages: [...snapshot.messages],
    pendingActions: [...snapshot.pendingActions],
    conversationId: snapshot.conversationId,
    error: webChatLoadError ?? snapshot.error,
    isLoading: !webChatLoadError && (isWebChatLoading || snapshot.status === "loading"),
    isRunning: snapshot.run.isRunning,
    typing: snapshot.run.typing,
    conversationStatus: snapshot.conversationStatus,
    run: snapshot.run,
    pagination: paginationWithFetch,
    isRecovering: snapshot.isRecovering,
    catchUpError: snapshot.catchUpError,
    refetch,
    sendMessage,
    respondToAction,
    sendAction,
    retryMessage,
    startNewConversation
  };
};
export {
  useWebChat
};
//# sourceMappingURL=useWebChat.js.map